import { filter, find, isEmpty, map } from 'lodash';
import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { ordersApi } from 'api';
import { FORM_FIELD_FIXED_LENGTH } from 'constants/form-field.constants';
import {
  CustomerDataCollectionOrder,
  OrderStatus,
  PaymentType,
  QueryKey,
} from 'enums';
import { FormField, Order, PaymentFormValues, TradeMethod } from 'types';

type UsePaymentFormInput = {
  orderId: string;
  selectedTradeMethod: TradeMethod | undefined;
  order: Order | undefined;
  isAmobile: boolean;
  paymentFormValues: PaymentFormValues;
};

export type UsePaymentFormResult = {
  customerFields: FormField[];
  requiredCustomerFields: Array<keyof PaymentFormValues>;
  isCustomerDataConfirmed: boolean;
  isCardInfoConfirmDisabled: boolean;
  customerDataBeforePayment: boolean;
  paymentFieldsLoading: boolean;
};

export const usePaymentForm = ({
  orderId,
  selectedTradeMethod,
  order,
  isAmobile,
  paymentFormValues,
}: UsePaymentFormInput): UsePaymentFormResult => {
  const { data: paymentFields, isLoading: paymentFieldsLoading } = useQuery(
    QueryKey.OrderPaymentFields,
    () => ordersApi.getPaymentFields(orderId),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: order?.status === OrderStatus.CustomerConfirm,
    },
  );

  const customerFields = useMemo((): FormField[] => {
    if (!selectedTradeMethod) {
      return [];
    }

    return filter(
      map(selectedTradeMethod.customerFields, (field) => {
        const override = find(paymentFields, { name: field.name });
        return { ...field, ...override };
      }),
      (field) =>
        !field.hidden ||
        (field.name === 'customerBank' &&
          order?.payment?.type === PaymentType.SBP &&
          isAmobile),
    );
  }, [paymentFields, selectedTradeMethod, isAmobile, order?.payment?.type]);

  const requiredCustomerFields = useMemo((): Array<keyof PaymentFormValues> => {
    if (isEmpty(customerFields)) {
      return [];
    }
    return customerFields
      .filter((field) => field.required)
      .map((field) => field.name) as Array<keyof PaymentFormValues>;
  }, [customerFields]);

  const isCustomerDataConfirmed = useMemo(() => {
    if (
      isAmobile &&
      order?.payment?.type === PaymentType.SBP &&
      !order?.payment?.customerBank
    ) {
      return false;
    }
    if (!order?.payment?.type && !selectedTradeMethod) {
      return false;
    }
    return requiredCustomerFields.every((field) => order?.payment?.[field]);
  }, [order?.payment, isAmobile, requiredCustomerFields, selectedTradeMethod]);

  const isCardInfoConfirmDisabled = useMemo(() => {
    if (!selectedTradeMethod) {
      return false;
    }
    return customerFields.some((field) => {
      if (!field.required) {
        return false;
      }
      const value =
        paymentFormValues[field.name as keyof PaymentFormValues] ?? '';

      if (!value) {
        return true;
      }

      const fixedLength = FORM_FIELD_FIXED_LENGTH[field.type];

      if (fixedLength && value.length !== fixedLength) {
        return true;
      }
      if (field.maxLength && value.length > field.maxLength) {
        return true;
      }
      if (field.pattern && !new RegExp(field.pattern).test(value)) {
        return true;
      }
      return false;
    });
  }, [customerFields, paymentFormValues, selectedTradeMethod]);

  const customerDataBeforePayment = useMemo(
    () =>
      order?.shop.customerDataCollectionOrder ===
      CustomerDataCollectionOrder.BeforePayment,
    [order?.shop.customerDataCollectionOrder],
  );

  return {
    customerFields,
    requiredCustomerFields,
    isCustomerDataConfirmed,
    isCardInfoConfirmDisabled,
    customerDataBeforePayment,
    paymentFieldsLoading,
  };
};
